import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  mergeClasses,
  Option,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
  DismissRegular,
} from "@fluentui/react-icons";
import { updateCategory } from "api";
import { ALLOWED_FILE_EXTENSIONS, ConstantValues } from "common/constants";
import { AttachmentsHandler } from "components/attachmentsHandler/AttachmentsHandler";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { FileDropzone } from "components/fileDropzone/FileDropzone";
import { FileWithId } from "models/FileWithId";
import { ICategory } from "models/ICategory";
import { IFile } from "models/IFile";
import { ITag } from "models/ITag";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { uploadFilesToStorage } from "utils/blobUtils";
import { generateId } from "utils/generalUtils";
import {
  clearAttachments,
  removeAttachment,
  setAttachments,
} from "../../../redux/currentAttachments";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { FieldError } from "components/fieldError/FieldError";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";
import { useEditTagStyles } from "./EditTag.styles";
import { IEditTagProps } from "./EditTag.types";

type TabType = "details" | "attachments";

export const EditTag = (props: IEditTagProps): JSX.Element => {
  const styles = useEditTagStyles();
  const sharedContentStyles = useSharedDialogContentStyles();
  const dropdownStyles = useSharedDropdownStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const attachments: FileWithId[] = useSelector(
    (state: RootState) => state.currentAttachments.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<TabType>("details");
  const [updatedTag, setUpdatedTag] = useState<ITag>({
    id: props.tag?.id ?? ConstantValues.EMPTY_STRING,
    name: props.tag?.name ?? ConstantValues.EMPTY_STRING,
    description: props.tag?.description ?? ConstantValues.EMPTY_STRING,
    isPublic: props.tag?.isPublic ?? false,
    category: props.tag?.category,
    displayName: props.tag?.displayName ?? ConstantValues.EMPTY_STRING,
    createdAt: props.tag?.createdAt ?? Math.floor(Date.now() / 1000),
    updatedAt: props.tag?.updatedAt ?? Math.floor(Date.now() / 1000),
    userId: props.tag?.userId ?? ConstantValues.EMPTY_STRING,
    attachments: props.tag?.attachments ?? [],
  });

  const [initializedAttachments, setInitializedAttachments] = useState(false);

  useEffect(() => {
    if (
      !initializedAttachments &&
      props.tag?.attachments &&
      attachments.length === 0
    ) {
      const convertedFiles: FileWithId[] = props.tag.attachments.map(
        (attachment) => ({
          file: new File([], attachment.filename),
          id: attachment.id ?? ConstantValues.EMPTY_STRING,
        }),
      );
      dispatch(setAttachments(convertedFiles));
      setInitializedAttachments(true);
    }
  }, [
    props.tag,
    props.tag?.attachments,
    dispatch,
    attachments.length,
    initializedAttachments,
  ]);

  const handleChange = (key: keyof ITag, value: any): void => {
    setUpdatedTag((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const validateDetailsFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (!updatedTag.name.trim()) {
      errors.name = "Title is required";
    } else if (updatedTag.name.trim().length > 50) {
      errors.name = "Title cannot be more than 50 characters";
    } else {
      const nameLowerCase = updatedTag.name.trim().toLowerCase();
      const existingTag = updatedTag.category?.tags.find(
        (tag) => tag.name.trim().toLowerCase() === nameLowerCase,
      );
      if (!props.tag && existingTag) {
        errors.name = `A knowledge component with the title "${updatedTag.name}" already exists in this category`;
      } else if (existingTag && existingTag.id !== updatedTag.id) {
        errors.name = `A knowledge component with the title "${updatedTag.name}" already exists`;
      }
    }

    if (!updatedTag.description.trim()) {
      errors.description = "Description is required";
    }

    if (!updatedTag.category) {
      errors.category = "Category is required";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    return true;
  };

  const handleAttachmentsChange = (
    initialFiles: FileWithId[],
    updatedFiles: FileWithId[],
    remove?: boolean,
  ): void => {
    let finalFiles: FileWithId[] = updatedFiles;

    if (remove) {
      const removedFiles = initialFiles.filter(
        (initialFile) =>
          !updatedFiles.some(
            (updatedFile) => updatedFile.id === initialFile.id,
          ),
      );

      removedFiles.forEach((file) =>
        dispatch(removeAttachment(file.file.name)),
      );

      finalFiles = updatedFiles.filter(
        (file) => !removedFiles.some((removed) => removed.id === file.id),
      );
    }

    const convertedFiles: IFile[] = finalFiles.map((file) => {
      const fallback = updatedTag.attachments?.find(
        (a) => a.id === file.id || a.filename === file.file.name,
      );

      return {
        filename: file.file.name,
        id: file.id,
        url: fallback?.url ?? ConstantValues.EMPTY_STRING,
      };
    });

    setUpdatedTag((prevTag) => ({
      ...prevTag,
      attachments: convertedFiles,
    }));
  };

  const handleProceedToAttachments = (): void => {
    if (validateDetailsFields()) {
      setActiveTab("attachments");
    }
  };

  const handleBackToDetails = (): void => {
    setActiveTab("details");
    setFieldErrors({});
  };

  const handleSave = async (): Promise<void> => {
    if (attachments.length === 0) {
      setFieldErrors({ attachments: "At least one attachment is required" });
      setActiveTab("attachments");
      return;
    }

    setIsLoading(true);

    try {
      const isNew =
        !updatedTag.id || updatedTag.id === ConstantValues.EMPTY_STRING;
      const tagId = isNew
        ? generateId()
        : (updatedTag.id ?? ConstantValues.EMPTY_STRING);

      const filesToUpload = attachments.filter(
        (att) => att.file instanceof File && att.file.size > 0,
      );

      let uploadedFiles: IFile[] = [];
      if (filesToUpload.length > 0) {
        uploadedFiles = await uploadFilesToStorage(filesToUpload, "tag", tagId);
      }

      const mergedAttachments: IFile[] = attachments.map((att) => {
        const uploaded = uploadedFiles.find(
          (f) => f.filename === att.file?.name,
        );
        if (uploaded) {
          return uploaded;
        }

        const fallback = updatedTag.attachments?.find(
          (a) => a.id === att.id || a.filename === att.file?.name,
        );

        return {
          id: att.id,
          filename:
            att.file?.name ?? fallback?.filename ?? ConstantValues.EMPTY_STRING,
          url: fallback?.url ?? ConstantValues.EMPTY_STRING,
        };
      });

      const displayName: string =
        currentUser?.displayName || ConstantValues.EMPTY_STRING;
      const finalTag: ITag = {
        ...updatedTag,
        id: tagId,
        updatedAt: Math.floor(Date.now() / 1000),
        displayName,
        attachments: mergedAttachments,
      };

      const newCategory: ICategory | undefined = updatedTag.category;

      if (!newCategory) {
        throw new Error("Updated knowledge component must have a category.");
      }

      const originalCategory = props.tag?.category;

      if (originalCategory && originalCategory.id !== newCategory.id) {
        const oldCategoryToUpdate: ICategory = {
          ...originalCategory,
          tags: originalCategory.tags.filter((t) => t.id !== finalTag.id),
        };

        await updateCategory({ category: oldCategoryToUpdate });
      }

      const updatedTags = newCategory.tags.some((tag) => tag.id === finalTag.id)
        ? newCategory.tags.map((tag) =>
            tag.id === finalTag.id ? finalTag : tag,
          )
        : [...newCategory.tags, finalTag];

      const newCategoryToUpdate: ICategory = {
        ...newCategory,
        tags: updatedTags,
      };

      await updateCategory({ category: newCategoryToUpdate });
      await queryClient.invalidateQueries("categories");

      dispatch(clearAttachments());
      props.onTagUpdate(finalTag);
      props.onDismiss(true);
    } catch (error) {
      console.error("Error saving knowledge component:", error);
      setFieldErrors({
        general: "An error occurred while saving. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    dispatch(clearAttachments());
    props.onDismiss(false);
  };

  const dialogTitle = props.tag
    ? "Update Knowledge Component"
    : "Add Knowledge Component";

  const handleTabClick = (tabKey: string): void => {
    if (tabKey === "attachments" && !validateDetailsFields()) {
      return;
    }
    setActiveTab(tabKey as TabType);
    setFieldErrors({});
  };

  const hasIncompleteDetails =
    !updatedTag.name.trim() ||
    !updatedTag.description.trim() ||
    !updatedTag.category;

  const isEditMode = !!props.tag;
  const saveTooltip = isEditMode
    ? "Update Knowledge Component"
    : "Add Knowledge Component";

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          handleClose();
        }
      }}
    >
      <DialogSurface
        className={mergeClasses(
          styles.dialogSurface,
          footerStyles.dialogSurface,
        )}
      >
        <DialogBody className={styles.dialogBody}>
          <DialogTitle
            action={
              <Button
                icon={<DismissRegular />}
                appearance="subtle"
                aria-label="Close"
                onClick={handleClose}
              />
            }
          >
            {dialogTitle}
          </DialogTitle>

          <DialogContent className={sharedContentStyles.tabbedDialogContent}>
            <DialogTabs
              tabs={[
                { key: "details", label: "Details" },
                { key: "attachments", label: "Attachments" },
              ]}
              activeTab={activeTab}
              onTabClick={handleTabClick}
            />
            <div
              className={mergeClasses(
                sharedContentStyles.formContainer,
                sharedContentStyles.scrollableContent,
              )}
            >
              {isLoading && (
                <div className={sharedContentStyles.formLoadingOverlay}>
                  <Spinner label="Saving knowledge component..." />
                </div>
              )}
              {activeTab === "details" && (
                <div className={sharedContentStyles.tabContent}>
                  <Field
                    label="Title"
                    required
                    validationState={fieldErrors.name ? "error" : "none"}
                    validationMessage={fieldErrors.name}
                  >
                    <Input
                      placeholder="Enter knowledge component title"
                      value={updatedTag.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      disabled={isLoading}
                    />
                  </Field>

                  <Field
                    label="Description"
                    required
                    validationState={fieldErrors.description ? "error" : "none"}
                    validationMessage={fieldErrors.description}
                  >
                    <Textarea
                      placeholder="Enter description"
                      value={updatedTag.description}
                      resize="vertical"
                      onChange={(e) =>
                        handleChange("description", e.target.value)
                      }
                      disabled={isLoading}
                    />
                  </Field>

                  <Field
                    label="Category"
                    required
                    validationState={fieldErrors.category ? "error" : "none"}
                    validationMessage={fieldErrors.category}
                  >
                    <Dropdown
                      className={dropdownStyles.dropdown}
                      listbox={{
                        className: mergeClasses(
                          dropdownStyles.listbox,
                          styles.dropdownListbox,
                        ),
                      }}
                      placeholder="Select category"
                      positioning={{
                        position: "below",
                        fallbackPositions: ["below"],
                      }}
                      value={
                        updatedTag.category?.title ??
                        ConstantValues.EMPTY_STRING
                      }
                      onOptionSelect={(_, data) => {
                        const selectedCategory = props.categories.find(
                          (c) => c.id === data.optionValue,
                        );
                        if (selectedCategory) {
                          handleChange("category", selectedCategory);
                        }
                      }}
                      disabled={isLoading}
                    >
                      {[...props.categories]
                        .sort((a, b) => a.title.localeCompare(b.title))
                        .map((category) => (
                          <Option key={category.id} value={category.id}>
                            {category.title}
                          </Option>
                        ))}
                    </Dropdown>
                  </Field>

                  <CustomToggle
                    checked={updatedTag.isPublic ?? false}
                    onChange={(checked) => handleChange("isPublic", checked)}
                    label="Is Public"
                    hint="Public components are visible to all users"
                    disabled={isLoading}
                  />
                </div>
              )}

              {activeTab === "attachments" && (
                <div className={sharedContentStyles.tabContent}>
                  <FileDropzone
                    onFilesSelected={(files) => {
                      const newFiles: FileWithId[] = files.map((file) => ({
                        file,
                        id: generateId(),
                      }));
                      const combinedFiles = [...attachments, ...newFiles];
                      dispatch(setAttachments(combinedFiles));
                      handleAttachmentsChange(attachments, combinedFiles);
                      if (fieldErrors.attachments) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          attachments: "",
                        }));
                      }
                    }}
                    acceptedExtensions={ALLOWED_FILE_EXTENSIONS}
                    multiple
                    disabled={isLoading}
                  />

                  {fieldErrors.attachments && (
                    <FieldError message={fieldErrors.attachments} />
                  )}

                  <div className={styles.attachmentsList}>
                    <AttachmentsHandler
                      onFilesChange={handleAttachmentsChange}
                      entityType="tag"
                      entity={{
                        name: updatedTag.name,
                        attachments: updatedTag.attachments || [],
                        description:
                          updatedTag.description || ConstantValues.EMPTY_STRING,
                      }}
                      hideAddButton
                      downloadFiles={Boolean(updatedTag.id)}
                    />
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </DialogBody>

        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            {activeTab === "attachments" && (
              <DialogFooterAction
                icon={<ArrowLeftRegular />}
                onClick={handleBackToDetails}
                tooltip="Back"
              />
            )}
            {activeTab === "details" && (
              <DialogFooterAction
                icon={<ArrowRightRegular />}
                onClick={handleProceedToAttachments}
                dimmed={hasIncompleteDetails}
                tooltip="Next"
              />
            )}
            {activeTab === "attachments" && (
              <DialogFooterAction
                icon={<CheckmarkRegular />}
                onClick={handleSave}
                dimmed={hasIncompleteDetails || attachments.length === 0}
                tooltip={saveTooltip}
              />
            )}
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
